import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  Typography,
  OutlinedInput,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  Close,
  FilterList,
  Clear,
  Search,
  Delete,
  Add,
  DateRange,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const CustomFilterModal = ({
  open,
  onClose,
  onApplyFilter,
  onClearFilter,
  data = [],
  sortableColumns = [],
}) => {
  const theme = useTheme();

  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedJabatan, setSelectedJabatan] = useState([]);
  const [selectedCabang, setSelectedCabang] = useState([]);

  // Date Range Filter
  const [tanggalAwal, setTanggalAwal] = useState(null);
  const [tanggalAkhir, setTanggalAkhir] = useState(null);

  // Sort state: array of { column, order }
  const [sortCriteria, setSortCriteria] = useState([
    { column: "", order: "asc" },
  ]);

  const clearAllFilters = () => {
    setSearchQuery("");
    setSelectedJabatan([]);
    setSelectedCabang([]);
    setTanggalAwal(null);
    setTanggalAkhir(null);
    setSortCriteria([{ column: "", order: "asc" }]);
    onClearFilter();
  };

  const getUniqueJabatan = () => {
    return [
      ...new Set(
        data
          .map((row) => row.nama_jabatan)
          .filter((val) => val !== null && val !== undefined && val !== "")
      ),
    ].sort();
  };

  const getUniqueCabang = () => {
    return [
      ...new Set(
        data
          .map((row) => row.cabang)
          .filter((val) => val !== null && val !== undefined && val !== "")
      ),
    ].sort();
  };

  // Hanya tampilkan kolom yang belum dipilih
  const getAvailableColumns = (currentIndex) => {
    const usedColumns = sortCriteria
      .filter((_, idx) => idx !== currentIndex)
      .map((c) => c.column);
    return sortableColumns.filter((col) => !usedColumns.includes(col.value));
  };

  const handleSortChange = (index, key, value) => {
    const updated = [...sortCriteria];
    updated[index][key] = value;
    setSortCriteria(updated);
  };

  const addSortCriteria = () => {
    setSortCriteria([...sortCriteria, { column: "", order: "asc" }]);
  };

  const removeSortCriteria = (index) => {
    const updated = [...sortCriteria];
    updated.splice(index, 1);
    setSortCriteria(updated.length ? updated : [{ column: "", order: "asc" }]);
  };

  const applyFilters = () => {
    const filterConfig = {
      search: searchQuery,
      filter: {
        cabang: selectedCabang,
        nama_jabatan: selectedJabatan,
      },
      dateRange: {
        tanggalAwal: tanggalAwal ? formatDateToYYYYMMDD(tanggalAwal) : null,
        tanggalAkhir: tanggalAkhir ? formatDateToYYYYMMDD(tanggalAkhir) : null,
      },
      sort: sortCriteria.filter((c) => c.column), // hanya kirim kriteria valid
    };
    onApplyFilter(filterConfig);
    onClose();
  };

  // Helper function to format date to YYYY-MM-DD
  const formatDateToYYYYMMDD = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (searchQuery) count++;
    if (selectedJabatan.length > 0) count++;
    if (selectedCabang.length > 0) count++;
    if (tanggalAwal || tanggalAkhir) count++;
    if (sortCriteria.some((c) => c.column)) count++;
    return count;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <FilterList sx={{ mr: 1 }} />
            <Typography variant="h6">
              Search, Filter & Sort Data Absensi
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ display: "flex", flexDirection: "column", gap: 3 }}
      >
        {/* SEARCH */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
            🔍 Search
          </Typography>
          <TextField
            fullWidth
            label="Cari ID atau Nama Karyawan"
            placeholder="Ketik ID atau Nama Karyawan..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <Search sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
            size="small"
          />
          {searchQuery && (
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                bgcolor: "primary.main",
                color: "white",
                borderRadius: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: "bold", display: "block" }}
              >
                🔍 Pencarian Aktif: "{searchQuery}"
              </Typography>
            </Box>
          )}
        </Box>

        {/* <Divider /> */}

        {/* FILTER */}
        {/* <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
            🧭 Filter Kategori
          </Typography>
          <Grid container spacing={2}>
            {getUniqueJabatan().length > 0 && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter Jabatan</InputLabel>
                  <Select
                    multiple
                    value={selectedJabatan}
                    onChange={(e) =>
                      setSelectedJabatan(
                        typeof e.target.value === "string"
                          ? e.target.value.split(",")
                          : e.target.value
                      )
                    }
                    input={<OutlinedInput label="Filter Jabatan" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((val) => (
                          <Chip key={val} label={val} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {getUniqueJabatan().map((jabatan) => (
                      <MenuItem key={jabatan} value={jabatan}>
                        <Checkbox
                          checked={selectedJabatan.indexOf(jabatan) > -1}
                        />
                        <ListItemText primary={jabatan} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter Cabang</InputLabel>
                <Select
                  multiple
                  value={selectedCabang}
                  onChange={(e) =>
                    setSelectedCabang(
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value
                    )
                  }
                  input={<OutlinedInput label="Filter Cabang" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((val) => (
                        <Chip key={val} label={val} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {getUniqueCabang().map((cabang) => (
                    <MenuItem key={cabang} value={cabang}>
                      <Checkbox checked={selectedCabang.indexOf(cabang) > -1} />
                      <ListItemText primary={cabang} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box> */}

        <Divider />

        {/* SORT */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
            ↕️ Sort
          </Typography>
          {sortCriteria.map((criteria, index) => (
            <Grid container spacing={2} key={index} sx={{ mb: 1.5 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Kriteria {index + 1}</InputLabel>
                  <Select
                    value={criteria.column}
                    label={`Kriteria ${index + 1}`}
                    onChange={(e) =>
                      handleSortChange(index, "column", e.target.value)
                    }
                  >
                    <MenuItem value="">
                      <em>Pilih Kolom</em>
                    </MenuItem>
                    {getAvailableColumns(index).map((col) => (
                      <MenuItem key={col.value} value={col.value}>
                        {col.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small" disabled={!criteria.column}>
                  <InputLabel>Urutan</InputLabel>
                  <Select
                    value={criteria.order}
                    label="Urutan"
                    onChange={(e) =>
                      handleSortChange(index, "order", e.target.value)
                    }
                  >
                    <MenuItem value="asc">Menaik (A → Z)</MenuItem>
                    <MenuItem value="desc">Menurun (Z → A)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                sm={2}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {sortCriteria.length > 1 && (
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => removeSortCriteria(index)}
                  >
                    <Delete />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}

          {getAvailableColumns(-1).length > 0 && (
            <Button
              startIcon={<Add />}
              size="small"
              variant="contained"
              onClick={addSortCriteria}
              color="primary"
            >
              Tambah Kriteria Sort
            </Button>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button
          onClick={clearAllFilters}
          startIcon={<Clear />}
          variant="outlined"
          color="secondary"
        >
          Clear All
        </Button>
        <Box display="flex" gap={1}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={applyFilters}
            variant="contained"
            startIcon={<FilterList />}
            color="primary"
          >
            Apply {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CustomFilterModal;
